import { useState } from 'react'
import { Alert, Button, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconPackageOff } from '@tabler/icons-react'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { Callout } from '@/components/ui'
import { confirmarRecebimento, type Robo } from '@/api/robo'

/**
 * Passo 1 (spec 2026-09-27, 4.4). "Não recebi" pede confirmação, avisa quem
 * atribuiu (o backend faz isso, uma vez por atribuição) e deixa o wizard parado
 * aqui. "Recebi" continua disponível: é assim que a placa que chegou depois
 * destrava o resto.
 */
export function PassoRecebimento({ robo, aoConfirmar }: { robo: Robo; aoConfirmar: () => void }) {
  const qc = useQueryClient()
  const [confirmandoRecusa, setConfirmandoRecusa] = useState(false)
  const [enviando, setEnviando] = useState<'sim' | 'nao' | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const recusado = robo.recebimento?.estado === 'nao_recebido'
  const quem = robo.recebimento?.atribuido_por

  const responder = async (recebi: boolean) => {
    setErro(null)
    setEnviando(recebi ? 'sim' : 'nao')
    try {
      await confirmarRecebimento(recebi)
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
      setConfirmandoRecusa(false)
      if (recebi) aoConfirmar()
    } catch (err) {
      setErro(isAxiosError(err) && err.response?.status === 409
        ? 'O recebimento já foi confirmado. Recarregue a página.'
        : 'Não foi possível registrar a resposta agora. Tente de novo em instantes.')
    } finally {
      setEnviando(null)
    }
  }

  return (
    <Stack gap="md">
      <Text size="sm">
        Você recebeu o robô <b>{robo.name}</b>{robo.mac ? ` (${robo.mac})` : ''}?
      </Text>
      {quem && (
        <Text size="xs" c="dimmed">Atribuído a você por {quem}.</Text>
      )}

      {recusado && (
        <Callout variant="warning" title="Entrega não confirmada">
          Quem atribuiu foi avisado. Quando a placa chegar, volte aqui.
        </Callout>
      )}
      {erro && <Alert color="red" icon={<IconAlertTriangle size={18} />}>{erro}</Alert>}

      {confirmandoRecusa ? (
        <Stack gap="xs">
          <Text size="sm">
            Confirmar que o robô não chegou? {quem ?? 'Quem administra a frota'} vai ser avisado.
          </Text>
          <Group gap="sm">
            <Button color="red" variant="light" leftSection={<IconPackageOff size={16} />}
              loading={enviando === 'nao'} onClick={() => responder(false)}>
              Confirmar que não recebi
            </Button>
            <Button variant="subtle" onClick={() => setConfirmandoRecusa(false)}>Voltar</Button>
          </Group>
        </Stack>
      ) : (
        <Group gap="sm">
          <Button leftSection={<IconCheck size={16} />} loading={enviando === 'sim'}
            onClick={() => responder(true)}>
            Recebi
          </Button>
          {!recusado && (
            <Button variant="default" onClick={() => setConfirmandoRecusa(true)}>
              Não recebi
            </Button>
          )}
        </Group>
      )}
    </Stack>
  )
}
